import type { ChangeSetStatus, ChangeSetView, Step } from '../api/client';
import { internalRouteLabel, isInternalRoute } from '@stitch/pbx-adapter/catalog';
import { displayZone } from './time';

export type Tone = 'ok' | 'warn' | 'crit' | 'neutral' | 'brand';

export const STATUS: Record<ChangeSetStatus, { label: string; tone: Tone }> = {
  planned: { label: 'Waiting for approval', tone: 'brand' },
  applying: { label: 'Applying', tone: 'warn' },
  applied: { label: 'Applied', tone: 'ok' },
  rolled_back: { label: 'Rolled back', tone: 'warn' },
  needs_attention: { label: 'Needs attention', tone: 'crit' },
  stale: { label: 'Out of date', tone: 'neutral' },
  discarded: { label: 'Discarded', tone: 'neutral' },
  undone: { label: 'Undone', tone: 'neutral' }
};

export const REVERSIBILITY: Record<Step['reversibility'], { label: string; tone: Tone }> = {
  full: { label: 'Undo restores it', tone: 'ok' },
  'settings-only': { label: 'Partial undo', tone: 'warn' },
  none: { label: "Can't be undone", tone: 'crit' }
};

export const STEP_STATE: Record<Step['state'], { label: string; tone: Tone }> = {
  pending: { label: 'Pending', tone: 'neutral' },
  applied: { label: 'Done', tone: 'ok' },
  rolled_back: { label: 'Put back', tone: 'warn' },
  failed: { label: 'Failed', tone: 'crit' },
  skipped: { label: 'Skipped', tone: 'neutral' }
};

// Every time is shown in the display zone (the person's choice, else their company's): see lib/time.
const formats = new Map<string, Intl.DateTimeFormat>();
function fmt(name: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const timeZone = displayZone();
  const key = `${name}|${timeZone}`;
  let f = formats.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(undefined, { ...opts, timeZone });
    formats.set(key, f);
  }
  return f;
}
const dayKey = (d: Date) => fmt('key', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);

/** Which day a time falls on, in the display zone ("2026-10-26"), for grouping by day. */
export function dayOf(iso: string): string {
  return dayKey(new Date(iso));
}

/** "Today", "Yesterday", or "Monday, October 26" (with the year when it isn't this one). */
export function dayHeading(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const key = dayKey(d);
  if (key === dayKey(now)) return 'Today';
  if (key === dayKey(new Date(now.getTime() - 86_400_000))) return 'Yesterday';
  const year = fmt('year', { year: 'numeric' });
  const sameYear = year.format(d) === year.format(now);
  return fmt(sameYear ? 'weekday' : 'weekday-year', { weekday: 'long', month: 'long', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) }).format(d);
}

/** "3:46 PM" today, otherwise "Oct 21". */
export function shortWhen(iso: string): string {
  const d = new Date(iso);
  return dayKey(d) === dayKey(new Date()) ? clockTime(d) : fmt('day', { month: 'short', day: 'numeric' }).format(d);
}

/** "Oct 28" (with the year when it isn't this one). */
export function monthDay(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const year = fmt('year', { year: 'numeric' });
  const sameYear = year.format(d) === year.format(now);
  return fmt(sameYear ? 'month-day' : 'month-day-year', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) }).format(d);
}

export function fullWhen(iso: string): string {
  return fmt('full', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
}

export function dateOnly(iso: string): string {
  return fmt('date', { dateStyle: 'medium' }).format(new Date(iso));
}

/** "3:46 PM". */
export function clockTime(d: Date | string | number): string {
  return fmt('time', { hour: 'numeric', minute: '2-digit' }).format(new Date(d));
}

export function actorLabel(cs: Pick<ChangeSetView, 'actor' | 'source'>): string {
  if (cs.actor.type === 'leland') return `Leland, for ${cs.actor.onBehalfOf ?? 'the customer'}`;
  return cs.actor.name;
}

export function canUndo(cs: ChangeSetView, now = new Date()): boolean {
  return cs.status === 'applied' && (!cs.undoUntil || new Date(cs.undoUntil) > now);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

const FIELD_NAMES: Record<string, string> = {
  firstName: 'first name',
  lastName: 'last name',
  email: 'email',
  mobile: 'mobile number',
  exists: 'extension',
  'other settings': 'other settings'
};

export function fieldName(f: string): string {
  return FIELD_NAMES[f] ?? f;
}

/** A number as people should see it: platform trigger numbers (+1 899) as "Internal route 0142". */
export function shownNumber(n: string): string {
  return isInternalRoute(n) ? internalRouteLabel(n) : n;
}

/** A size in bytes, the way people read it ("1.4 GB"). */
export function sizeOf(n: number): string {
  if (n < 1024) return `${n} bytes`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}
